import { FinancialRepresentativeType } from '../../../../services/personApi';
import type { BasicFinancialRepresentativePayload, FinancialRepresentative } from '../../../../services/personApi';
import type { ErrorField } from '../../../../types/ErrorField';
import { isValidEmail } from '../../../../utils/EmailUtils';
import { formatCpf } from '../../../../utils/taxIdUtils';
import type { SearchablePickerOption } from '../../../organisms/SearchablePicker';

export const MINIMUM_SEARCH_LENGTH = 3;
const FULL_NAME_MAX_LENGTH = 150;
const EMAIL_MAX_LENGTH = 255;
const MOBILE_PATTERN = /^\d{2}9\d{8}$/;
const CPF_LIKE_PATTERN = /^[\d.\-\s]+$/;
const BASIC_TYPE_FORMATTED = 'Cadastro básico';

const isBasic = (item: FinancialRepresentative): boolean => item.type === FinancialRepresentativeType.BASIC;

const toCandidateOption = (candidate: FinancialRepresentative): SearchablePickerOption => ({
    id: representativeKey(candidate),
    label: candidate.name,
    hint: formatCpf(candidate.socialId ?? ''),
});

const normalizeName = (value: string): string => value.trim().split(/\s+/).filter(Boolean).join(' ');
const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const identityKey = (fullName: string, email: string, phone: string): string =>
    [normalizeName(fullName).toLowerCase(), normalizeEmail(email), onlyDigits(phone)].join('|');

/**
 * The key of a list item: the individual, the saved basic link or the local key of a new basic one.
 */
export const representativeKey = (item: FinancialRepresentative): string =>
    item.individualUuid ?? item.basicFinancialRepresentativeId ?? item.localKey ?? '';

/**
 * Adds a Financial Representative at the end of the list; an item already in it is not added again.
 */
export const addFinancialRepresentative = (
    list: FinancialRepresentative[],
    item: FinancialRepresentative,
): FinancialRepresentative[] => {
    const key = representativeKey(item);
    if (list.some((current) => representativeKey(current) === key)) return list;
    return [...list, item];
};

/**
 * Removes only the Financial Representative with the given key (see representativeKey).
 */
export const removeFinancialRepresentative = (
    list: FinancialRepresentative[],
    key: string,
): FinancialRepresentative[] => list.filter((item) => representativeKey(item) !== key);

/**
 * The individual ids sent as financialRepresentativeIds in PUT /legal-persons/{id}; basic ones are left out.
 */
export const toFinancialRepresentativeIds = (list: FinancialRepresentative[]): string[] =>
    list.filter((item) => !isBasic(item) && item.individualUuid).map((item) => item.individualUuid as string);

/**
 * True when both lists hold the same individuals, in any order.
 */
export const sameFinancialRepresentativeIds = (a: string[], b: string[]): boolean => {
    const left = new Set(a);
    const right = new Set(b);
    if (left.size !== right.size) return false;
    return Array.from(left).every((id) => right.has(id));
};

/**
 * Search results offered by the picker, without the people already in the list.
 */
export const toCandidateOptions = (
    candidates: FinancialRepresentative[],
    selected: FinancialRepresentative[],
): SearchablePickerOption[] => {
    const selectedKeys = new Set(selected.map(representativeKey));
    return candidates
        .filter((candidate) => !selectedKeys.has(representativeKey(candidate)))
        .map(toCandidateOption);
};

export interface BasicFinancialRepresentativeDraft {
    fullName: string;
    email: string;
    phone: string;
}

/**
 * The basic representatives sent as basicFinancialRepresentatives in PUT /legal-persons/{id}: a saved
 * one goes with its id (kept as it is), a new one without.
 */
export const toBasicFinancialRepresentatives = (list: FinancialRepresentative[]): BasicFinancialRepresentativePayload[] =>
    list.filter(isBasic).map((item) => ({
        ...(item.basicFinancialRepresentativeId ? { id: item.basicFinancialRepresentativeId } : {}),
        fullName: item.name,
        email: item.email ?? '',
        phone: item.phone ?? '',
    }));

/**
 * True when both lists hold the same basic representatives, in any order: saved ones by id, new ones by data.
 */
export const sameBasicFinancialRepresentatives = (
    a: BasicFinancialRepresentativePayload[],
    b: BasicFinancialRepresentativePayload[],
): boolean => {
    const keyOf = (item: BasicFinancialRepresentativePayload) => item.id ?? identityKey(item.fullName, item.email, item.phone);
    const left = a.map(keyOf).sort();
    const right = b.map(keyOf).sort();
    return left.length === right.length && left.every((key, index) => key === right[index]);
};

/**
 * True when the list already has a basic representative with the same name (any case), e-mail and phone,
 * the same rule the person applies on save.
 */
export const isDuplicateBasic = (list: FinancialRepresentative[], draft: BasicFinancialRepresentativeDraft): boolean => {
    const key = identityKey(draft.fullName, draft.email, draft.phone);
    return list.some((item) => isBasic(item) && identityKey(item.name, item.email ?? '', item.phone ?? '') === key);
};

/**
 * Field errors of the basic registration form: all three required, e-mail format and a mobile phone
 * (DDD + number starting with 9).
 */
export const validateBasicRepresentative = (draft: BasicFinancialRepresentativeDraft): ErrorField[] => {
    const errors: ErrorField[] = [];
    const fullName = normalizeName(draft.fullName);
    const email = normalizeEmail(draft.email);
    if (!fullName) errors.push({ fieldName: 'fullName', message: 'Informe o nome.' });
    else if (fullName.length > FULL_NAME_MAX_LENGTH) errors.push({ fieldName: 'fullName', message: 'O nome deve ter até 150 caracteres.' });
    if (!isValidEmail(email) || email.length > EMAIL_MAX_LENGTH) errors.push({ fieldName: 'email', message: 'E-mail inválido.' });
    if (!MOBILE_PATTERN.test(onlyDigits(draft.phone))) errors.push({ fieldName: 'phone', message: 'Celular inválido: informe DDD e número começando com 9.' });
    return errors;
};

/**
 * The name the basic form starts with: the searched term, unless it is a CPF (digits, dots and dash).
 */
export const initialBasicName = (term: string): string => {
    const trimmed = term.trim();
    return CPF_LIKE_PATTERN.test(trimmed) ? '' : normalizeName(trimmed);
};

/**
 * True when the search found nobody for a term long enough to have been searched.
 */
export const shouldOfferBasicRegistration = (term: string, results: unknown[]): boolean =>
    term.trim().length >= MINIMUM_SEARCH_LENGTH && results.length === 0;

/**
 * The BASIC list item of a draft not saved yet.
 */
export const toNewBasicFinancialRepresentative = (draft: BasicFinancialRepresentativeDraft, localKey: string): FinancialRepresentative => ({
    type: FinancialRepresentativeType.BASIC,
    typeFormatted: BASIC_TYPE_FORMATTED,
    basicFinancialRepresentativeId: null,
    individualUuid: null,
    socialId: null,
    name: normalizeName(draft.fullName),
    phone: onlyDigits(draft.phone),
    email: normalizeEmail(draft.email),
    localKey,
});
